import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  sendPasswordResetEmail,
  signInAnonymously,
  signOut
} from 'firebase/auth';
import { useEffect, useMemo, useState } from 'react';

import { AuthContext } from '../context/AuthContext';

import { useFirebase } from '../hooks/useFirebase';

import { useAppUser } from '../api/hooks/user/useAppUser';
import { initialize, clearAccessToken } from '../api/tools/tools';
import { trackEvent } from '../utils/analytics/metaPixel';

export const AuthStatus = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  INVALID_USERNAME: 'INVALID_USERNAME',
  INCORRECT_PASSWORD: 'INCORRECT_PASSWORD',
  INVALID_CREDENTIAL: 'INVALID_CREDENTIAL',
  USERNAME_IN_USE: 'USERNAME_IN_USE',
  RESET_EMAIL_SENT: 'RESET_EMAIL_SENT'
};

export const AuthErrorType = {
  ERROR: 'ERROR',
  WARNING: 'WARNING',
  INFO: 'INFO'
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState();
  const [pageIndex, setPageIndex] = useState(0);
  const [authStatus, setAuthStatus] = useState(undefined);
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const { appUser, loading: isAdminUserLoading, refetch: fetchAppUser } = useAppUser(user);
  const isAdmin = useMemo(() => (appUser ? appUser.adminPanel : false), [appUser]);
  const [authReady, setAuthReady] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestName, setGuestName] = useState('');

  const { auth } = useFirebase();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      if (authUser) {
        setUser(authUser);
        initialize(authUser.accessToken);
        await fetchAppUser(authUser);
      } else {
        setUser(null);
        clearAccessToken();
      }
      setAuthReady(true);
      setPageLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [auth, fetchAppUser]);

  const clearAuthError = () => {
    setAuthStatus(null);
  };

  const processError = (error) => {
    setFormData((prevData) => ({ ...prevData, password: '' }));

    //eslint-disable-next-line
    console.log(error);

    if (
      error.code === 'auth/user-not-found' ||
      error.code === 'auth/invalid-email' ||
      error.code === 'auth/wrong-password' ||
      error.code === 'auth/invalid-credential'
    ) {
      setAuthStatus({
        type: AuthErrorType.INFO,
        message: 'Invalid username/password! Please try again.',
        status: AuthStatus.INVALID_CREDENTIAL,
        title: 'Input Error'
      });
    } else if (error.code === 'auth/email-already-in-use') {
      setAuthStatus({
        type: AuthErrorType.WARNING,
        message: 'Account for email already exists',
        status: AuthStatus.USERNAME_IN_USE,
        title: 'Input Error'
      });
    } else if (error.code === 'auth/weak-password') {
      setAuthStatus({
        type: AuthErrorType.WARNING,
        message:
          'Your password is too weak. ' +
          'Please use a minimum of 6 characters, including at least one uppercase letter, ' +
          'one lowercase letter, one number, and one special character',
        status: AuthStatus.USERNAME_IN_USE,
        title: 'Input Error'
      });
    } else if (error.code === 'auth/missing-email') {
      setAuthStatus({
        type: AuthErrorType.WARNING,
        message: 'Please enter a valid email address',
        status: AuthStatus.INVALID_USERNAME,
        title: 'Input Error'
      });
    } else if (error.code === 'auth/network-request-failed') {
      setAuthStatus({
        type: AuthErrorType.WARNING,
        message: 'There was a problem connecting to the internet. Please try again.',
        status: AuthStatus.ERROR,
        title: 'Network Error'
      });
    } else if (error.code === 'auth/unverified-email') {
      setAuthStatus({
        type: AuthErrorType.WARNING,
        message: 'Email not verified',
        status: AuthStatus.ERROR
      });
    } else if (error.code === 'auth/too-many-requests') {
      setAuthStatus({
        message: 'Too many login attempts. Please try again later.',
        status: AuthStatus.ERROR
      });
    } else if (
      error.code === 'auth/popup-blocked' ||
      error.code === 'auth/popup-closed-by-user' ||
      error.code === 'auth/cancelled-popup-request'
    ) {
      setAuthStatus({
        message: 'Authentication failed, please try again!',
        status: AuthStatus.ERROR
      });
    } else {
      setAuthStatus({
        message: 'There was an error trying to authenticate your account.',
        status: AuthStatus.ERROR
      });
    }
  };

  const signInWithEmail = async (email, password, onComplete) => {
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
      onComplete(AuthStatus.SUCCESS);
      setFormData({});
    } catch (error) {
      processError(error, onComplete);
    } finally {
      setLoading(false);
    }
  };

  const registerWithEmail = async (email, password, onComplete) => {
    try {
      setLoading(true);
      await createUserWithEmailAndPassword(auth, email, password);
      trackEvent('CompleteRegistration', {}, { email });
      clear();
      onComplete(AuthStatus.SUCCESS);
    } catch (error) {
      processError(error, onComplete);
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      if (getAdditionalUserInfo(result)?.isNewUser) {
        trackEvent(
          'CompleteRegistration',
          {},
          { email: result.user.email, name: result.user.displayName }
        );
      }
      clear();
    } catch (error) {
      processError(error);
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
      setAuthStatus({
        type: AuthErrorType.INFO,
        message: 'A reset email has been sent. Please check your inbox for further instructions.',
        status: AuthStatus.RESET_EMAIL_SENT,
        title: 'Reset email sent'
      });
    } catch (error) {
      processError(error);
    } finally {
      setLoading(false);
    }
  };

  const signInAsGuest = async (email, phone, name) => {
    try {
      setLoading(true);
      await signInAnonymously(auth);
      setGuestEmail(email);
      setGuestPhone(phone);
      setGuestName(name);
    } catch (error) {
      processError(error);
    } finally {
      setLoading(false);
    }
  };

  const signOutUser = () => {
    setGuestEmail('');
    setGuestPhone('');
    setGuestName('');
    clear();
    signOut(auth);
  };

  const clear = () => {
    clearFormData();
    clearAuthError();
    setPageIndex(0);
  };

  const clearFormData = () => {
    setFormData(() => {});
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        user,
        authReady,
        pageIndex,
        setPageIndex,
        authError: authStatus,
        clearAuthError,
        pageLoading: isAdminUserLoading || pageLoading,
        loading,
        formData,
        setFormData,
        signInWithGoogle,
        signInWithEmail,
        signInAsGuest,
        signOut: signOutUser,
        registerWithEmail,
        clearFormData,
        forgotPassword,
        isAdmin,
        guestEmail,
        guestPhone,
        guestName
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
